import { useState, useEffect } from 'react';
import { Search, RefreshCw, ChevronDown, ChevronRight, Users, Shield, AlertTriangle, Check, Clock, DollarSign, FileText, Database, TrendingUp, Building2, X, Download, Loader2, Eye, MapPin, Globe, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const STATUS_COLORS = {
  PENDING_KYC: { label: 'Pending KYC', color: 'text-gray-400 bg-gray-500/20' },
  PENDING_APPROVAL: { label: 'Pending Approval', color: 'text-amber-400 bg-amber-500/20' },
  APPROVED: { label: 'Approved', color: 'text-emerald-400 bg-emerald-500/20' },
  REJECTED: { label: 'Rejected', color: 'text-red-400 bg-red-500/20' },
  SUSPENDED: { label: 'Suspended', color: 'text-orange-400 bg-orange-500/20' },
};

const DOC_STATUS_BADGE = {
  VERIFIED: { label: 'Verified', cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  PENDING: { label: 'Pending', cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  REJECTED: { label: 'Rejected', cls: 'text-red-400 bg-red-500/10 border-red-500/30' },
  NOT_UPLOADED: { label: 'Not Uploaded', cls: 'text-gray-500 bg-gray-500/10 border-gray-500/30' },
};

const INV_STATE_COLORS = {
  INTEREST: 'text-gray-400', EOI_PAID: 'text-blue-400', DOC_SENT: 'text-amber-400',
  DOC_SIGNED: 'text-purple-400', CAPITAL_PAID: 'text-emerald-400', COMPLETED: 'text-gray-300',
  CANCELLED: 'text-red-400', REFUNDED: 'text-orange-400',
};

const AUDIT_ACTION_COLORS = {
  REGISTERED: 'text-blue-400 bg-blue-500/20', KYC_SUBMITTED: 'text-amber-400 bg-amber-500/20',
  APPROVED: 'text-emerald-400 bg-emerald-500/20', REJECTED: 'text-red-400 bg-red-500/20',
  SUSPENDED: 'text-orange-400 bg-orange-500/20', RERA_RENEWED: 'text-blue-400 bg-blue-500/20',
  RERA_EXPIRED: 'text-red-400 bg-red-500/20', REFERRAL: 'text-purple-400 bg-purple-500/20',
  COMMISSION_PAID: 'text-emerald-400 bg-emerald-500/20', REMINDER_SENT: 'text-amber-400 bg-amber-500/20',
};

const ALL_INV_STATES = ['INTEREST', 'EOI_PAID', 'DOC_SENT', 'DOC_SIGNED', 'CAPITAL_PAID', 'COMPLETED', 'CANCELLED', 'REFUNDED'];

const formatINR = (val) => {
  if (!val) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return `${(val / 1000).toFixed(0)} K`;
};

export default function AdminBrokersTab() {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [stats, setStats] = useState({});
  const [invStateFilter, setInvStateFilter] = useState({});
  const [reviewingDoc, setReviewingDoc] = useState(null); // { brokerId, docType, action }
  const [reviewForm, setReviewForm] = useState({ expiryDate: '', rejectionReason: '', remarks: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [approvalModal, setApprovalModal] = useState(null); // { brokerId, action: 'approve'|'reject' }
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status_filter', statusFilter);
      if (search) params.set('search', search);
      const res = await api.get(`/admin/brokers/consolidated?${params}`);
      setBrokers(res.data.brokers || []);
      setStats(res.data.stats || {});
    } catch { 
      setBrokers([]);
      setStats({});
      toast.error('Failed to load broker data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const handleSearch = (e) => { e.preventDefault(); fetchData(); };

  // Document review
  const handleDocReview = async (brokerId, docType, approved) => {
    setActionLoading(true);
    try {
      const body = { approved };
      if (approved && reviewForm.expiryDate) body.expiryDate = reviewForm.expiryDate;
      if (!approved) body.rejectionReason = reviewForm.rejectionReason || 'Document rejected';
      await api.post(`/admin/broker/${brokerId}/documents/${docType}/review`, body);
      toast.success(`${docType.toUpperCase()} ${approved ? 'approved' : 'rejected'}`);
      setReviewingDoc(null);
      setReviewForm({ expiryDate: '', rejectionReason: '', remarks: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to review document');
    } finally { setActionLoading(false); }
  };

  // Broker application approve/reject
  const handleBrokerApproval = async (brokerId, approved) => {
    setActionLoading(true);
    try {
      const body = { approved, remarks: reviewForm.remarks };
      if (!approved) body.rejectionReason = reviewForm.rejectionReason || 'Application rejected';
      await api.post(`/admin/brokers-legacy/${brokerId}/approve`, body);
      toast.success(`Broker ${approved ? 'approved' : 'rejected'} successfully`);
      setApprovalModal(null);
      setReviewForm({ expiryDate: '', rejectionReason: '', remarks: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update broker status');
    } finally { setActionLoading(false); }
  };

  const getDocBadge = (status) => DOC_STATUS_BADGE[status] || DOC_STATUS_BADGE.NOT_UPLOADED;

  const statCards = [
    { key: '', label: 'Total', count: stats.total || 0, color: 'text-white' },
    { key: 'PENDING_APPROVAL', label: 'Pending', count: stats.pendingApproval || 0, color: 'text-amber-400' },
    { key: 'APPROVED', label: 'Approved', count: stats.approved || 0, color: 'text-emerald-400' },
    { key: 'REJECTED', label: 'Rejected', count: stats.rejected || 0, color: 'text-red-400' },
  ];

  return (
    <div className="p-8" data-testid="brokers-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl text-white font-light">Broker Management</h1>
          <p className="text-sm text-gray-500 mt-1">Verification, referred investors, and commission reconciliation</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchData} className="p-2 border border-white/10 text-gray-400 hover:text-white" data-testid="refresh-brokers">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-6">
        {statCards.map(s => (
          <button key={s.key} onClick={() => setStatusFilter(statusFilter === s.key ? '' : s.key)}
            className={`flex-1 p-3 border transition-all ${statusFilter === s.key ? 'border-white/30 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
            <p className={`text-lg font-mono ${s.color}`}>{s.count}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6 flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white focus:outline-none"
            placeholder="Search by name, email, RERA..." data-testid="search-brokers" />
        </div>
        <button type="submit" className="px-4 py-2 border border-white/10 text-sm text-gray-400 hover:text-white">Search</button>
      </form>

      {/* Broker list */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading brokers...</div>
      ) : brokers.length === 0 ? (
        <div className="text-center py-16 text-gray-500">No brokers found</div>
      ) : (
        <div className="space-y-2">
          {brokers.map(broker => {
            const isExpanded = expanded[broker.brokerId];
            const bStatus = broker.brokerStatus || broker.status || 'PENDING_KYC';
            const statusCfg = STATUS_COLORS[bStatus] || STATUS_COLORS.PENDING_KYC;
            const comm = broker.commission || {};
            const ver = broker.verification || {};
            const docs = broker.documents || {};
            const company = broker.company || {};
            const stateFilter = invStateFilter[broker.brokerId] || '';

            const filteredInvestors = stateFilter
              ? (broker.referredInvestors || []).filter(inv => inv.state === stateFilter)
              : (broker.referredInvestors || []);

            // Count investors per state for chips
            const stateCounts = {};
            (broker.referredInvestors || []).forEach(inv => {
              stateCounts[inv.state] = (stateCounts[inv.state] || 0) + 1;
            });

            return (
              <div key={broker.brokerId} className="border border-white/10" data-testid={`broker-row-${broker.brokerId}`}>
                {/* Header Row — shows doc status at a glance */}
                <button onClick={() => toggle(broker.brokerId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors text-left">
                  <div className="flex items-center gap-4">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white">{broker.name}</span>
                        <span className={`text-xs px-2.5 py-0.5 ${statusCfg.color}`}>{statusCfg.label}</span>
                        {ver.reraExpired && (
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> RERA Expired
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{broker.email} | {broker.companyName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Document status indicators */}
                    <div className="flex items-center gap-2" data-testid={`doc-indicators-${broker.brokerId}`}>
                      {['rera', 'gst'].map(dt => {
                        const dStatus = docs[dt]?.status || 'NOT_UPLOADED';
                        const badge = getDocBadge(dStatus);
                        return (
                          <span key={dt} className={`text-[10px] px-2 py-0.5 border rounded-sm font-mono uppercase ${badge.cls}`}>
                            {dt} {dStatus === 'VERIFIED' ? '\u2713' : dStatus === 'REJECTED' ? '\u2717' : dStatus === 'PENDING' ? '\u25CF' : '\u2014'}
                          </span>
                        );
                      })}
                    </div>
                    <div className="text-right text-xs">
                      <span className="text-gray-400">{broker.summary?.totalInvestors || 0} investors</span>
                      <p className="font-mono text-white">{formatINR(broker.summary?.totalCapital)}</p>
                    </div>
                    {comm.pending > 0 && (
                      <span className="text-xs text-amber-400 font-mono">{formatINR(comm.pending)} pending</span>
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-white/5 px-6 py-4 space-y-5 bg-white/[0.02]">

                    {/* Application-level rejection banner */}
                    {broker.appRejectionReason && bStatus === 'REJECTED' && (
                      <div className="p-3 border border-red-500/20 bg-red-500/5 flex items-start gap-2" data-testid={`app-rejection-${broker.brokerId}`}>
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-red-400 font-medium">Application Rejected</p>
                          <p className="text-xs text-red-400/70 mt-1">{broker.appRejectionReason}</p>
                        </div>
                      </div>
                    )}

                    {/* Company Details */}
                    <div data-testid={`company-${broker.brokerId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5" /> Company Details
                      </h3>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-gray-600">Company</p>
                          <p className="text-white">{company.name || broker.companyName || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">RERA Number</p>
                          <p className="text-white font-mono text-xs">{company.reraNumber || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">GST</p>
                          <p className="text-white font-mono text-xs">{company.gstNumber || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">PAN</p>
                          <p className="text-white font-mono text-xs">{company.panNumber || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">City / State</p>
                          <p className="text-white">{[company.city, company.state].filter(Boolean).join(', ') || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Address</p>
                          <p className="text-white text-xs">{company.address || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Website</p>
                          <p className="text-white text-xs">{company.website || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Registered</p>
                          <p className="text-white text-xs">{broker.registeredAt ? new Date(broker.registeredAt).toLocaleDateString() : broker.createdAt ? new Date(broker.createdAt).toLocaleDateString() : '\u2014'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Documents — RERA & GST with approve/reject */}
                    <div data-testid={`documents-${broker.brokerId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> Documents
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {['rera', 'gst'].map(dtype => {
                          const doc = docs[dtype] || {};
                          const dStatus = doc.status || 'NOT_UPLOADED';
                          const badge = getDocBadge(dStatus);
                          const isReviewing = reviewingDoc?.brokerId === broker.brokerId && reviewingDoc?.docType === dtype;

                          return (
                            <div key={dtype} className="p-4 bg-white/5 border border-white/10" data-testid={`doc-${dtype}-${broker.brokerId}`}>
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm text-white font-medium uppercase">{dtype}</span>
                                <span className={`text-xs px-2 py-0.5 border rounded-sm ${badge.cls}`}>{badge.label}</span>
                              </div>

                              {dStatus === 'NOT_UPLOADED' ? (
                                <p className="text-xs text-gray-600">No document uploaded by broker</p>
                              ) : (
                                <>
                                  {/* View Document Button */}
                                  {doc.fileId && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/broker/${broker.brokerId}/documents/${dtype}/download`, '_blank');
                                      }}
                                      className="mb-3 px-3 py-1.5 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors flex items-center gap-1.5 w-full justify-center"
                                      data-testid={`view-doc-${dtype}-${broker.brokerId}`}
                                    >
                                      <Eye className="w-3 h-3" />
                                      View Document
                                    </button>
                                  )}
                                  
                                  <div className="text-xs text-gray-400 space-y-1">
                                    <p>File: <span className="text-white">{doc.fileName}</span></p>
                                    {doc.fileSize && <p>Size: {doc.fileSize}</p>}
                                    {doc.uploadedAt && <p>Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}</p>}
                                    {doc.expiryDate && (
                                      <p>Expires: <span className={new Date(doc.expiryDate) < new Date(Date.now() + 30*24*60*60*1000) ? 'text-yellow-400' : 'text-white'}>{new Date(doc.expiryDate).toLocaleDateString()}</span></p>
                                    )}
                                    {doc.reviewedAt && <p>Reviewed: {new Date(doc.reviewedAt).toLocaleDateString()}</p>}
                                  </div>

                                  {/* Rejection reason display */}
                                  {dStatus === 'REJECTED' && doc.rejectionReason && (
                                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                                      Reason: {doc.rejectionReason}
                                    </div>
                                  )}

                                  {/* Review actions */}
                                  {isReviewing ? (
                                    <div className="mt-3 space-y-2">
                                      {reviewingDoc.action === 'approve' ? (
                                        <>
                                          <label className="text-xs text-gray-400">Expiry Date</label>
                                          <input type="date" value={reviewForm.expiryDate}
                                            onChange={e => setReviewForm(f => ({ ...f, expiryDate: e.target.value }))}
                                            className="w-full bg-black border border-white/20 text-white text-xs px-2 py-1.5 focus:outline-none"
                                            data-testid={`expiry-input-${dtype}`} />
                                          <div className="flex gap-2">
                                            <button onClick={() => handleDocReview(broker.brokerId, dtype, true)}
                                              disabled={actionLoading}
                                              className="px-3 py-1 text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 disabled:opacity-50"
                                              data-testid={`confirm-approve-${dtype}`}>
                                              {actionLoading ? 'Saving...' : 'Confirm Approve'}
                                            </button>
                                            <button onClick={() => setReviewingDoc(null)} className="px-3 py-1 text-xs text-gray-400 border border-white/10">Cancel</button>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <label className="text-xs text-gray-400">Rejection Reason</label>
                                          <input type="text" value={reviewForm.rejectionReason}
                                            onChange={e => setReviewForm(f => ({ ...f, rejectionReason: e.target.value }))}
                                            placeholder="Reason for rejection..."
                                            className="w-full bg-black border border-white/20 text-white text-xs px-2 py-1.5 focus:outline-none"
                                            data-testid={`reject-reason-${dtype}`} />
                                          <div className="flex gap-2">
                                            <button onClick={() => handleDocReview(broker.brokerId, dtype, false)}
                                              disabled={actionLoading}
                                              className="px-3 py-1 text-xs bg-red-500/20 text-red-400 border border-red-500/30 disabled:opacity-50"
                                              data-testid={`confirm-reject-${dtype}`}>
                                              {actionLoading ? 'Saving...' : 'Confirm Reject'}
                                            </button>
                                            <button onClick={() => setReviewingDoc(null)} className="px-3 py-1 text-xs text-gray-400 border border-white/10">Cancel</button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ) : (dStatus === 'PENDING' || dStatus === 'REJECTED') && (
                                    <div className="flex gap-2 mt-3">
                                      <button onClick={() => { setReviewingDoc({ brokerId: broker.brokerId, docType: dtype, action: 'approve' }); setReviewForm({ expiryDate: '', rejectionReason: '', remarks: '' }); }}
                                        className="px-3 py-1 text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                        data-testid={`approve-doc-${dtype}-${broker.brokerId}`}>Approve</button>
                                      <button onClick={() => { setReviewingDoc({ brokerId: broker.brokerId, docType: dtype, action: 'reject' }); setReviewForm({ expiryDate: '', rejectionReason: '', remarks: '' }); }}
                                        className="px-3 py-1 text-xs bg-red-500/20 text-red-400 border border-red-500/30"
                                        data-testid={`reject-doc-${dtype}-${broker.brokerId}`}>Reject</button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* KYC & Bank Details */}
                    <div data-testid={`verification-${broker.brokerId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5" /> KYC & Bank Verification
                      </h3>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-gray-600">PAN</p>
                          <p className="text-white">{ver.panNumber || '\u2014'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">RERA</p>
                          <p className={`${ver.reraExpired ? 'text-red-400' : 'text-white'}`}>
                            {ver.reraNumber || '\u2014'}
                            {ver.reraExpiry && <span className="text-xs text-gray-600 ml-2">(exp: {new Date(ver.reraExpiry).toLocaleDateString()})</span>}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Bank</p>
                          <p className="text-white">{ver.bankDetails?.bankName || '\u2014'} {ver.bankDetails?.accountNumber ? `***${ver.bankDetails.accountNumber.slice(-4)}` : ''}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">IFSC</p>
                          <p className="text-white font-mono text-xs">{ver.bankDetails?.ifscCode || '\u2014'}</p>
                        </div>
                      </div>

                      {/* Broker approval actions */}
                      {(bStatus === 'PENDING_APPROVAL' || bStatus === 'REJECTED') && (
                        <div className="flex gap-2 mt-3">
                          {approvalModal?.brokerId === broker.brokerId ? (
                            <div className="w-full p-3 border border-white/10 bg-white/[0.02] space-y-2">
                              <p className="text-xs text-white">{approvalModal.action === 'approve' ? 'Approve this broker?' : 'Reject this broker?'}</p>
                              {approvalModal.action === 'approve' ? (
                                <input type="text" value={reviewForm.remarks}
                                  onChange={e => setReviewForm(f => ({ ...f, remarks: e.target.value }))}
                                  placeholder="Approval remarks (optional)"
                                  className="w-full bg-black border border-white/20 text-white text-xs px-2 py-1.5 focus:outline-none" />
                              ) : (
                                <input type="text" value={reviewForm.rejectionReason}
                                  onChange={e => setReviewForm(f => ({ ...f, rejectionReason: e.target.value }))}
                                  placeholder="Rejection reason (required)"
                                  className="w-full bg-black border border-red-500/30 text-white text-xs px-2 py-1.5 focus:outline-none"
                                  data-testid={`app-reject-reason-${broker.brokerId}`} />
                              )}
                              <div className="flex gap-2">
                                <button onClick={() => handleBrokerApproval(broker.brokerId, approvalModal.action === 'approve')}
                                  disabled={actionLoading}
                                  className={`px-3 py-1.5 text-xs border disabled:opacity-50 ${
                                    approvalModal.action === 'approve'
                                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                                  }`} data-testid={`confirm-app-${approvalModal.action}-${broker.brokerId}`}>
                                  {actionLoading ? 'Processing...' : `Confirm ${approvalModal.action === 'approve' ? 'Approval' : 'Rejection'}`}
                                </button>
                                <button onClick={() => setApprovalModal(null)} className="px-3 py-1.5 text-xs text-gray-400 border border-white/10">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <button onClick={() => { setApprovalModal({ brokerId: broker.brokerId, action: 'approve' }); setReviewForm({ expiryDate: '', rejectionReason: '', remarks: '' }); }}
                                className="px-3 py-1.5 text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                data-testid={`approve-${broker.brokerId}`}>Approve Broker</button>
                              <button onClick={() => { setApprovalModal({ brokerId: broker.brokerId, action: 'reject' }); setReviewForm({ expiryDate: '', rejectionReason: '', remarks: '' }); }}
                                className="px-3 py-1.5 text-xs bg-red-500/20 text-red-400 border border-red-500/30"
                                data-testid={`reject-${broker.brokerId}`}>Reject Broker</button>
                            </>
                          )}
                        </div>
                      )}
                      {bStatus === 'APPROVED' && ver.reraExpired && (
                        <div className="mt-3 p-3 border border-red-500/20 bg-red-500/5 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                          <p className="text-sm text-red-400">RERA expired — broker cannot refer new investors until renewed</p>
                        </div>
                      )}
                    </div>

                    {/* Commission */}
                    <div data-testid={`commission-${broker.brokerId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <DollarSign className="w-3.5 h-3.5" /> Commission Reconciliation
                      </h3>
                      <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                        <div className="p-3 bg-white/5 border border-white/10">
                          <p className="text-xs text-gray-600">Expected</p>
                          <p className="font-mono text-white text-lg">{formatINR(comm.expected)}</p>
                        </div>
                        <div className="p-3 bg-white/5 border border-white/10">
                          <p className="text-xs text-gray-600">Paid</p>
                          <p className="font-mono text-emerald-400 text-lg">{formatINR(comm.paid)}</p>
                        </div>
                        <div className="p-3 bg-white/5 border border-white/10">
                          <p className="text-xs text-gray-600">Pending</p>
                          <p className={`font-mono text-lg ${comm.pending > 0 ? 'text-amber-400' : 'text-gray-400'}`}>{formatINR(comm.pending)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Referred Investors with state filter */}
                    <div data-testid={`referred-${broker.brokerId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" /> Referred Investors
                        <span className="ml-auto text-xs text-gray-600">{broker.referredInvestors?.length || 0} total</span>
                      </h3>

                      {/* State filter chips */}
                      {(broker.referredInvestors?.length || 0) > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`state-filter-${broker.brokerId}`}>
                          <button onClick={() => setInvStateFilter(f => ({ ...f, [broker.brokerId]: '' }))}
                            className={`text-[10px] px-2 py-1 border rounded-sm ${!stateFilter ? 'border-white/30 bg-white/10 text-white' : 'border-white/10 text-gray-500 hover:text-white'}`}>
                            All ({broker.referredInvestors?.length || 0})
                          </button>
                          {ALL_INV_STATES.filter(s => stateCounts[s]).map(s => (
                            <button key={s} onClick={() => setInvStateFilter(f => ({ ...f, [broker.brokerId]: f[broker.brokerId] === s ? '' : s }))}
                              className={`text-[10px] px-2 py-1 border rounded-sm ${stateFilter === s ? 'border-white/30 bg-white/10 text-white' : `border-white/10 ${INV_STATE_COLORS[s] || 'text-gray-500'} hover:bg-white/5`}`}>
                              {s.replace(/_/g, ' ')} ({stateCounts[s]})
                            </button>
                          ))}
                        </div>
                      )}

                      {filteredInvestors.length > 0 ? (
                        <div className="space-y-1.5">
                          {filteredInvestors.map(inv => (
                            <div key={inv.investmentId} className="flex items-center justify-between py-2.5 px-4 bg-white/5 text-sm border border-white/5">
                              <div className="flex items-center gap-4">
                                <span className="text-white">{inv.investorName}</span>
                                <span className="text-gray-500 text-xs">{inv.unitName || inv.unitId}</span>
                                <span className={`text-xs px-2 py-0.5 border ${INV_STATE_COLORS[inv.state] || 'text-gray-400'} border-current/20`}>{inv.state?.replace(/_/g, ' ')}</span>
                              </div>
                              <div className="flex items-center gap-6 text-xs">
                                <div>
                                  <span className="text-gray-500">EOI: </span>
                                  <span className="font-mono text-white">{formatINR(inv.amounts?.eoiPaid)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Capital: </span>
                                  <span className="font-mono text-white">{formatINR(inv.amounts?.capitalPaid)}</span>
                                </div>
                                <span className="font-mono text-[#4ADE80]">{formatINR(inv.amounts?.totalCommitment)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3 border border-white/5 bg-white/[0.02]">
                          <p className="text-sm text-gray-500">{stateFilter ? `No investors in ${stateFilter.replace(/_/g, ' ')} state` : 'No referred investors yet'}</p>
                        </div>
                      )}
                    </div>

                    {/* Audit trail */}
                    <div data-testid={`audit-${broker.brokerId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" /> Audit Trail
                        <span className="ml-auto text-xs text-gray-600">{broker.auditTrail?.length || 0} events</span>
                      </h3>
                      {broker.auditTrail?.length > 0 ? (
                        <div className="space-y-0 border border-white/5 max-h-48 overflow-y-auto">
                          {broker.auditTrail.map((event, i) => (
                            <div key={i} className={`flex items-start gap-3 px-4 py-2.5 text-sm ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                              <span className="text-gray-600 text-xs w-32 flex-shrink-0 font-mono pt-0.5">
                                {event.timestamp ? new Date(event.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : ''}
                                {event.timestamp && <span className="text-gray-700 ml-1">{new Date(event.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
                              </span>
                              <span className={`text-xs px-2 py-0.5 flex-shrink-0 ${AUDIT_ACTION_COLORS[event.action] || 'text-gray-400 bg-white/5'}`}>
                                {event.action?.replace(/_/g, ' ')}
                              </span>
                              <span className="text-gray-400 text-xs flex-1">{event.details || event.reason || ''}</span>
                              {event.performedBy && <span className="text-gray-600 text-xs flex-shrink-0">by {typeof event.performedBy === 'object' ? (event.performedBy.type || event.performedBy.id || 'system') : event.performedBy}</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3 border border-white/5 bg-white/[0.02]">
                          <p className="text-sm text-gray-500">No activity recorded</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
